import * as React from 'react';

import { parseZonedDateTime } from '@rematter/common/dateTime';
import {
  DataGridColumn,
  LocalizedDate,
  Money,
  Button,
  Menu,
  maybeEmptyInfo,
  MeasuredValue,
} from '@rematter/ui-web';
import {
  LucideArrowRight,
  LucideDownload,
  LucideEllipsis,
  LucidePrinter,
} from 'lucide-react';

import { PurchaseOrderRecord } from '../../../_graphql/queryPurchaseOrderList';
import { customerPortalRoutes } from '../../../routes';
import { PurchaseOrderStatusBadge } from '../PurchaseOrderBadges';

import { useTranslations } from '~/modules/customer-portal/i18n/useTranslations';

export type UsePurchaseOrderListColumnsArgs = {
  onCellClick: (args: { row: PurchaseOrderRecord }) => void;
  navigate: (
    route: string,
    options?: { params?: Record<string, string> },
  ) => void;
  menuRefs: (id: string) => React.RefObject<HTMLButtonElement | null>;
  onPrint: (id: string) => void;
  onDownload: (id: string) => void;
};

export const usePurchaseOrderListColumns = ({
  onCellClick,
  navigate,
  menuRefs,
  onPrint,
  onDownload,
}: UsePurchaseOrderListColumnsArgs): DataGridColumn<PurchaseOrderRecord>[] => {
  const t = useTranslations();

  return React.useMemo(
    () => [
      {
        key: 'createdOn',
        header: t.purchaseOrderListView.header.createdOn,
        width: 140,
        onClick: onCellClick,
        cell: (cell: { row: PurchaseOrderRecord }) => (
          <LocalizedDate
            date={parseZonedDateTime(cell.row.timestamps.createdAt)}
            dateStyle="medium"
          />
        ),
      },
      {
        key: 'supplier',
        header: t.purchaseOrderListView.header.supplier,
        width: 230,
        onClick: onCellClick,
        cell: (cell: { row: PurchaseOrderRecord }) => (
          <span className="truncate">
            {maybeEmptyInfo(cell.row.supplier?.name)}
          </span>
        ),
      },
      {
        key: 'poNumber',
        header: t.purchaseOrderListView.header.poNumber,
        width: 160,
        onClick: onCellClick,
        cell: (cell: { row: PurchaseOrderRecord }) =>
          cell.row.purchaseOrderNumber
            ? `#${cell.row.purchaseOrderNumber}`
            : '—',
      },
      {
        key: 'counterpartySoNumber',
        header: t.purchaseOrderListView.header.counterpartySoNumber,
        width: 140,
        onClick: onCellClick,
        cell: (cell: { row: PurchaseOrderRecord }) =>
          `#${cell.row.salesOrderNumber}`,
      },
      {
        key: 'numberOfMaterials',
        header: t.purchaseOrderListView.header.numberOfMaterials,
        width: 120,
        onClick: onCellClick,
        cell: (cell: { row: PurchaseOrderRecord }) =>
          cell.row.numberOfMaterials,
      },
      {
        key: 'netWeight',
        header: t.purchaseOrderListView.header.netWeight,
        width: 120,
        onClick: onCellClick,
        cell: (cell: { row: PurchaseOrderRecord }) => (
          <MeasuredValue {...cell.row.netWeight} />
        ),
      },
      {
        key: 'shippedLoads',
        header: t.purchaseOrderListView.header.shippedLoads,
        width: 100,
        onClick: onCellClick,
        cell: (cell: { row: PurchaseOrderRecord }) =>
          cell.row.shippedLoadsCount,
      },
      {
        key: 'fulfilledPercentage',
        header: t.purchaseOrderListView.header.fulfilledPercentage,
        width: 100,
        onClick: onCellClick,
        cell: (cell: { row: PurchaseOrderRecord }) =>
          `${cell.row.fulfilledPercentage}%`,
      },
      {
        key: 'status',
        header: t.purchaseOrderListView.header.status,
        width: 100,
        onClick: onCellClick,
        cell: (cell: { row: PurchaseOrderRecord }) => (
          <PurchaseOrderStatusBadge status={cell.row.status} />
        ),
      },
      {
        key: 'totalAmount',
        header: t.purchaseOrderListView.header.totalAmount,
        width: 120,
        onClick: onCellClick,
        cell: (cell: { row: PurchaseOrderRecord }) => (
          <Money {...cell.row.totalPrice} />
        ),
      },
      {
        key: 'actions',
        header: t.purchaseOrderListView.header.actions,
        width: 80,
        cell: (cell: { row: PurchaseOrderRecord }) => (
          <Menu
            placement="bottom end"
            menuClassName="min-w-30"
            trigger={
              <Button
                ref={menuRefs(cell.row.id)}
                variant="outline"
                color="neutral"
              >
                <LucideEllipsis size={16} />
              </Button>
            }
            items={[
              {
                label: 'View Details',
                icon: <LucideArrowRight />,
                onAction: () =>
                  navigate(customerPortalRoutes.purchaseOrderDetails, {
                    params: { purchaseOrderId: cell.row.id },
                  }),
              },
              'separator',
              {
                label: 'Print Documents',
                icon: <LucidePrinter />,
                onAction: () => onPrint(cell.row.id),
              },
              {
                label: 'Download Documents',
                icon: <LucideDownload />,
                onAction: () => onDownload(cell.row.id),
              },
            ]}
          />
        ),
        pinned: 'right',
      },
    ],
    [t, onCellClick, navigate, menuRefs, onPrint, onDownload],
  );
};
